import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, tap, Subject, defer, timer, EMPTY, expand, last, switchMap, map } from 'rxjs';
import { ApiConstants } from '../constants/api-constants';
import { IEQMSScopeLookUpDetails } from '../models/scopes/eqms-Scope-lookup-details';
import { EQMSScopeHierarchyWithButtonFlags } from '../models/scopes/eqms-scope-hierarchy-with-button-flags';
import { EQMSScopeHierarchy } from '../models/scopes/eqms-scope-hierarchy';
import { TreeNode } from 'primeng/api';

@Injectable({
  providedIn: 'root'
})
export class EqmsScopeManagementService {
  treeData = {};
  treeDataForManagement = {};
  eQMSScopes: IEQMSScopeLookUpDetails[] = [];
  scopeUpdated$ = new Subject<boolean>();
  private readonly ROOT_SCOPE_ID = 1;

  constructor(private httpClient: HttpClient) { }

  getEQMSScope(): Observable<any> {
    this.treeData=[];
    let observable: Observable<any>;
    if (Object.keys(this.treeData).length > 0) {
      observable = of(this.treeData);
    }
    else {
      observable = this.httpClient.get<any>(ApiConstants.EQMSScopeManagement_Api)
        .pipe(
          tap(treeData => this.treeData = treeData)
        );
    }
    return observable;
  }

  geteQMSScopeToDisplay() : Observable<EQMSScopeHierarchyWithButtonFlags> {
    let observable: Observable<EQMSScopeHierarchyWithButtonFlags>;
    if (Object.keys(this.treeData).length > 0) {
      observable = of(this.treeData as EQMSScopeHierarchyWithButtonFlags);
    }
    else {
      observable = this.httpClient.get<EQMSScopeHierarchyWithButtonFlags>(ApiConstants.EQMSScopeManagement_ToDisplay_Api)
        .pipe(
          tap(treeData => this.treeData = treeData)
        );
    }
    return observable;
  }

  geteQMSScopeLookup(): Observable<IEQMSScopeLookUpDetails[]> {
    let observable: Observable<IEQMSScopeLookUpDetails[]>;
    if (this.eQMSScopes.length > 0) {
      observable = of(this.eQMSScopes);
    }
    else {
      observable = this.httpClient.get<IEQMSScopeLookUpDetails[]>(ApiConstants.EQMSScopeManagementLookup_Api)
        .pipe(
          tap(eQMSScopes => this.eQMSScopes = eQMSScopes)
        );
    }
    return observable;
  }

  getEQMSScopeForManagement(): Observable<any> {
    let observable: Observable<any>;
    if (Object.keys(this.treeDataForManagement).length > 0) {
      observable = of(this.treeDataForManagement);
    }
    else {
      observable = this.httpClient.get<any>(ApiConstants.EQMSScopesForManagement_Api)
        .pipe(
          tap(treeData => this.treeDataForManagement = treeData)
        );
    }
    return observable;
  }

  clearTreeData(): void {
    this.treeData = {};
  }

  /**
   * Repeatedly re-fetches the scope hierarchy (bypassing the FE cache) until the given scope
   * satisfies isUpdated, or maxAttempts is reached. Needed because scope writes are processed
   * asynchronously (event bus + persistence worker), so the update API response does not
   * guarantee the DB/cache has been updated yet.
   */
  pollUntilScopeUpdated(
    scopeId: number,
    isUpdated: (scope: EQMSScopeHierarchy) => boolean,
    maxAttempts: number = 10,
    intervalMs: number = 1000
  ): Observable<EQMSScopeHierarchyWithButtonFlags> {
    let attempt = 0;
    const fetchFresh = () => {
      this.clearTreeData();
      return this.geteQMSScopeToDisplay();
    };

    return defer(fetchFresh).pipe(
      expand((response) => {
        attempt++;
        const scope = this.findScopeInHierarchy(response.scopeHierarchy, scopeId);
        const isDone = attempt >= maxAttempts || (!!scope && isUpdated(scope));
        return isDone ? EMPTY : timer(intervalMs).pipe(switchMap(fetchFresh));
      }),
      last(),
      map((response) => {
        // Reaching maxAttempts does not mean the predicate matched; verify before returning
        const scope = this.findScopeInHierarchy(response.scopeHierarchy, scopeId);
        if (!scope || !isUpdated(scope)) {
          throw new Error(`Scope ${scopeId} was not updated after ${maxAttempts} attempts`);
        }
        return response;
      })
    );
  }

  private findScopeInHierarchy(node: EQMSScopeHierarchy, scopeId: number): EQMSScopeHierarchy | null {
    if (!node) {
      return null;
    }
    if (node.eQMSScopeId === scopeId) {
      return node;
    }
    for (const child of node.children || []) {
      const found = this.findScopeInHierarchy(child, scopeId);
      if (found) {
        return found;
      }
    }
    return null;
  }

  updateScopeHierarchy(request: any): Observable<any> {
    // Clear cached data to force refresh after update
    this.treeData = {};
    this.treeDataForManagement = {};
    
    return this.httpClient.post<any>(ApiConstants.EQMSScopeHierarchyUpdate_Api, request);
  }

  /**
     * Recursively collapses nodes
     * @param nodes Tree nodes to collapse
     * @param isRoot Whether this is the root level (root stays expanded)
     */
    public recursiveCollapse(nodes: TreeNode[], isRoot: boolean = false): void {
      nodes.forEach(node => {
        const scopeId = this.getScopeIdFromNode(node);
        
        // Keep root node expanded, collapse all others
        if (isRoot && scopeId === this.ROOT_SCOPE_ID) {
          node.expanded = true;
        } else {
          node.expanded = false;
        }
        
        if (node.children && node.children.length > 0) {
          this.recursiveCollapse(node.children, false);
        }
      });
    }

    /**
   * Recursively expands all nodes
   */
  public recursiveExpand(nodes: TreeNode[]): void {
    nodes.forEach(node => {
      node.expanded = true;
      if (node.children && node.children.length > 0) {
        this.recursiveExpand(node.children);
      }
    });
  }

    private getScopeIdFromNode(node: TreeNode): number {
        return node.data?.eQMSScopeId || node.data?.scopeId || 0;
      }

    geteQMSScopesLinkedToUserWithDocumentAndTrainingAlongClosestAncestor(): Observable<any> {
      return this.httpClient.get<any>(ApiConstants.GeteQMSScopesLinkedToUserWithDocumentAndTrainingAlongClosestAncestor_Api);
    }

    geteQMSScopeAncestors(scopeIds: number[]): Observable<any> {
      return this.httpClient.post<any>(ApiConstants.GeteQMSScopeAncestors_Api, scopeIds);
    }
}
